// Outcome histogram for the Goal-Based Risk Alignment Tool.
//
// Uses Recharts (per product spec) so the bars get a free hover
// tooltip + responsive resizing. The component is purely presentational
// — it receives the pre-binned ``HistogramBin[]`` from the backend
// (which keeps the math + bin choice consistent across every caller)
// and just renders them.
//
// A vertical reference line marks the goal target; bins to the right
// of the target are coloured emerald (success), bins to the left are
// coloured slate (miss). This gives the user an at-a-glance read on
// the success probability before they even look at the headline %.

import {
    Bar,
    BarChart,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { HistogramBin } from '../api/goalAlignmentApi';

interface OutcomeHistogramProps {
    bins: HistogramBin[];
    /** Goal target — a vertical reference line is drawn at this x. */
    target: number;
    /** Currency prefix for tooltip values. */
    currency?: string;
}

function formatMoneyCompact(value: number, currency = '$'): string {
    const sign = value < 0 ? '-' : '';
    const v = Math.abs(value);
    if (v >= 1_000_000) return `${sign}${currency}${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${sign}${currency}${(v / 1_000).toFixed(0)}k`;
    return `${sign}${currency}${v.toFixed(0)}`;
}

interface TooltipPayloadItem {
    payload?: HistogramBin;
}

interface TooltipProps {
    active?: boolean;
    payload?: TooltipPayloadItem[];
}

const HistogramTooltip = ({ active, payload }: TooltipProps) => {
    if (!active || !payload?.length) return null;
    const bin = payload[0].payload;
    if (!bin) return null;
    return (
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">
                {formatMoneyCompact(bin.lower)} – {formatMoneyCompact(bin.upper)}
            </p>
            <p className="muted">{bin.count} simulated paths</p>
        </div>
    );
};

export const OutcomeHistogram = ({
    bins,
    target,
    currency = '$',
}: OutcomeHistogramProps) => {
    // Index of the first bin whose lower edge sits at or beyond the
    // target — every bin from this index onward is a "success" bin
    // and gets the emerald fill. Pre-computed once per render so the
    // <Cell> map below stays cheap.
    const successFromIdx = bins.findIndex((b) => b.upper >= target);

    return (
        <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={bins}
                    margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
                >
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                        interval="preserveStartEnd"
                        // Show ~6 evenly-spaced ticks instead of all 30
                        // bin labels so they fit even at narrow widths.
                        ticks={
                            bins.length > 6
                                ? bins
                                      .filter(
                                          (_, i) =>
                                              i % Math.ceil(bins.length / 6) ===
                                              0,
                                      )
                                      .map((b) => b.label)
                                : bins.map((b) => b.label)
                        }
                    />
                    <YAxis
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                        allowDecimals={false}
                        width={32}
                    />
                    <Tooltip
                        content={<HistogramTooltip />}
                        cursor={{ fill: 'rgba(148, 163, 184, 0.15)' }}
                    />
                    <ReferenceLine
                        x={
                            // Snap the reference line to the bin label
                            // closest to the target so Recharts has a
                            // valid categorical x-coordinate.
                            successFromIdx >= 0
                                ? bins[successFromIdx]?.label
                                : bins[bins.length - 1]?.label
                        }
                        stroke="#0ea5e9"
                        strokeDasharray="4 4"
                        label={{
                            value: `Goal ${formatMoneyCompact(target, currency)}`,
                            position: 'top',
                            fill: '#0ea5e9',
                            fontSize: 11,
                            fontWeight: 700,
                        }}
                    />
                    <Bar
                        dataKey="count"
                        radius={[4, 4, 0, 0]}
                        isAnimationActive
                        animationDuration={500}
                    >
                        {bins.map((_, idx) => (
                            <Cell
                                key={idx}
                                fill={
                                    successFromIdx >= 0 && idx >= successFromIdx
                                        ? '#10b981' // emerald-500 — success
                                        : '#64748b' // slate-500 — miss
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default OutcomeHistogram;
